/**
 * ユーザーが選択したカスタム VRM アバターを IndexedDB に永続化するための薄いラッパー。
 * アプリを再読み込みしても選択が保持されるよう、VrmContext から利用される。
 * 依存パッケージを増やさないため、素の indexedDB API のみで実装する。
 */

const DB_NAME = 'vrma-hub';
const DB_VERSION = 1;
const STORE_NAME = 'settings';
const CUSTOM_VRM_KEY = 'customVrm';

export interface CustomVrmRecord {
  blob: Blob;
  name: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB のオープンに失敗しました'));
  });
}

/** カスタム VRM を IndexedDB に保存する（キー固定のため既存の保存分は上書きされる）。 */
export async function saveCustomVrm(blob: Blob, name: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const record: CustomVrmRecord = { blob, name };
      tx.objectStore(STORE_NAME).put(record, CUSTOM_VRM_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('カスタム VRM の保存に失敗しました'));
    });
  } finally {
    db.close();
  }
}

/** 保存済みのカスタム VRM を読み出す。未保存なら null を返す。 */
export async function loadCustomVrm(): Promise<CustomVrmRecord | null> {
  const db = await openDb();
  try {
    return await new Promise<CustomVrmRecord | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(CUSTOM_VRM_KEY);
      req.onsuccess = () => resolve((req.result as CustomVrmRecord | undefined) ?? null);
      req.onerror = () => reject(req.error ?? new Error('カスタム VRM の読み込みに失敗しました'));
    });
  } finally {
    db.close();
  }
}

/** 保存済みのカスタム VRM を削除する（未保存の場合も何もしない）。 */
export async function clearCustomVrm(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(CUSTOM_VRM_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('カスタム VRM の削除に失敗しました'));
    });
  } finally {
    db.close();
  }
}
